import {
    CARRIER_FILTER_QUERY,
    CHANNEL_FILTER_OPTIONS,
    CREATOR_FILTER_OPTIONS,
    EINVOICE_FILTER_OPTIONS,
    KIOTVIET_UNLINKED_FILTER_OPTIONS,
    LOT_DEBT_FILTER_OPTIONS,
    ORDER_PERIODS,
    PAYMENT_CHIP_OPTIONS,
    PERIOD_PRESET_OPTIONS,
    QUICK_SALE_FILTER_OPTIONS,
    RAW_STATUS_CODE,
    SETTLEMENT_INTENT_OPTIONS,
    SLA_STATE_FILTER_OPTIONS,
    STATUS_OPTIONS,
} from './orders-list-filter-options'
import type {
    FilterFieldDef,
    FilterValues,
} from '@/components/common/filters/types'
import type { ListOrdersParams } from '@/lib/api/sales-order-api'
import type { OrderPeriod } from './orders-list-filter-options'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import {
    MULTI_CHOICE_ALL,
    multiChoiceFilter,
} from '@/components/common/filters/multi-choice-filter'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { orderCodeFromPackingSlipQr } from '@/lib/scan-code'
import { useShippingCarriers } from '@/hooks/use-shipping-carriers-query'
import { getPresetRange } from '@/lib/date-presets'
import { m } from '@/lib/paraglide/messages'

/** Số dòng mỗi trang — tracking vị trí đơn được mở tính theo đúng số này. */
export const ORDERS_PER_PAGE = 20

export const periodLabel = (period: OrderPeriod) =>
    period === 'custom'
        ? m.order_period_custom()
        : (PERIOD_PRESET_OPTIONS().find((o) => o.value === period)?.label ??
          period)

const isPeriod = (raw: unknown): raw is OrderPeriod =>
    typeof raw === 'string' && ORDER_PERIODS.some((p) => p === raw)

/**
 * Khoảng thời gian theo ngày tạo. Mặc định "Tháng này" như KiotViet (owner
 * 27/09). "Tùy chỉnh" KHÔNG ghi lên URL: có date_from/date_to là tùy chỉnh,
 * nên link cũ (donut dashboard) giữ nguyên dạng mà vẫn đúng nghĩa.
 */
const periodFilter: FilterFieldDef<OrderPeriod> = {
    defaultValue: 'month',
    label: m.order_period_label,
    parse: (raw) => (isPeriod(raw) ? raw : undefined),
    serialize: (value) => (value === 'custom' ? undefined : value),
    // Tùy chỉnh đã có chip của từng ô ngày; thêm chip thời gian là lặp lại.
    chipLabel: (value) =>
        value === 'custom'
            ? undefined
            : `${m.order_period_label()}: ${periodLabel(value)}`,
    render: ({ id, value, onChange, surface }) =>
        selectFilter<OrderPeriod>({
            label: m.order_period_label,
            defaultValue: 'month',
            options: () => [
                ...PERIOD_PRESET_OPTIONS(),
                { value: 'custom', label: m.order_period_custom() },
            ],
        }).render({ id, value, onChange, surface }),
}

const orderSearchText = textFilter({
    label: m.order_search_label,
    placeholder: m.order_search_placeholder,
})

/**
 * Ô tìm đơn: nhân viên bấm vào ô rồi dùng máy quét quét QR phiếu đóng gói — máy
 * quét gõ nguyên URL, ở đây cắt còn mã đơn trước khi lên URL (ô nhập đồng bộ
 * lại theo giá trị đã cắt). Chỉ riêng ô này.
 */
const orderSearchFilter: FilterFieldDef<string> = {
    ...orderSearchText,
    render: (ctx) =>
        orderSearchText.render({
            ...ctx,
            onChange: (value) =>
                ctx.onChange(orderCodeFromPackingSlipQr(value)),
        }),
}

/** Ô lọc "Đặt qua": đơn bán lẻ thật theo kênh (storefront_orders.channel). */
const SHOP_CHANNEL_FILTER_OPTIONS = () => [
    { value: 'all', label: m.order_shop_channel_all() },
    { value: 'web', label: m.order_shop_channel_web() },
    { value: 'zalo_mini_app', label: m.order_shop_channel_miniapp() },
]

export const ORDER_LIST_FILTERS = defineListFilters(
    {
        q: orderSearchFilter,
        period: periodFilter,
        date_from: dateFilter({ label: m.report_from_date }),
        date_to: dateFilter({ label: m.report_to_date }),
        // Mặc định mọi trạng thái trừ "Đã hủy" (owner 27/09, như KiotViet).
        status: multiChoiceFilter({
            label: m.order_status,
            options: STATUS_OPTIONS,
            defaultValues: STATUS_OPTIONS()
                .map((o) => o.value)
                .filter((v) => v !== 'cancelled'),
            acceptUnknown: (raw) => RAW_STATUS_CODE.test(raw),
        }),
        // 'unpaid,partial' = "còn nợ" (FD-376) vẫn là một tổ hợp hợp lệ.
        payment_status: multiChoiceFilter({
            label: m.order_payment,
            options: PAYMENT_CHIP_OPTIONS,
            defaultValues: [],
            variant: 'chips',
        }),
        source_channel: selectFilter({
            label: m.order_channel,
            defaultValue: 'all',
            options: CHANNEL_FILTER_OPTIONS,
        }),
        // Báo cáo › Website / Zalo Mini App bấm dòng trạng thái mở danh sách
        // với bộ lọc này: chỉ đơn bán lẻ thật của kênh.
        shop_channel: selectFilter({
            label: m.order_shop_channel_filter_label,
            defaultValue: 'all',
            options: SHOP_CHANNEL_FILTER_OPTIONS,
        }),
        created_by: selectFilter({
            label: m.order_creator_filter_label,
            defaultValue: 'all',
            options: CREATOR_FILTER_OPTIONS,
        }),
        // Lọc theo ngày GIAO, tách khỏi ngày tạo; deep-link công nợ dashboard
        // dùng đúng cặp này (FD-376).
        delivered_from: dateFilter({ label: m.order_delivered_from }),
        delivered_to: dateFilter({ label: m.order_delivered_to }),
        einvoice_status: selectFilter({
            label: m.einvoice_filter_label,
            defaultValue: 'all',
            options: EINVOICE_FILTER_OPTIONS,
        }),
        settlement_intent: selectFilter({
            label: m.order_settlement_filter_label,
            defaultValue: 'all',
            options: SETTLEMENT_INTENT_OPTIONS,
        }),
        // Cùng định nghĩa với badge SLA ở cột "Hạn xử lý" (M-15).
        sla_state: selectFilter({
            label: m.order_sla_label,
            defaultValue: 'all',
            options: SLA_STATE_FILTER_OPTIONS,
        }),
        has_lot_debt: selectFilter({
            label: m.order_lot_debt_col,
            defaultValue: 'all',
            options: LOT_DEBT_FILTER_OPTIONS,
        }),
        kiotviet_unlinked: selectFilter({
            label: m.order_kiotviet_unlinked_filter_label,
            defaultValue: 'all',
            options: KIOTVIET_UNLINKED_FILTER_OPTIONS,
        }),
        quick_sale: selectFilter({
            label: m.order_quick_sale_filter_label,
            defaultValue: 'all',
            options: QUICK_SALE_FILTER_OPTIONS,
        }),
        // Lấy CẢ hãng đã ngừng dùng — đơn cũ của hãng đó vẫn phải lọc ra được.
        shipping_carrier_id: remoteSelectFilter({
            label: m.order_carrier_filter_label,
            allLabel: m.order_carrier_filter_all,
            useOptions: () => {
                const { data } = useShippingCarriers(CARRIER_FILTER_QUERY)
                return (data?.data ?? []).map((carrier) => ({
                    value: carrier.id,
                    label: carrier.name,
                }))
            },
        }),
    },
    {
        normalize: (values) => {
            const hasDates = values.date_from !== '' || values.date_to !== ''
            // Link cũ chỉ mang ngày: hiểu là tùy chỉnh.
            if (values.period === 'month' && hasDates)
                return { ...values, period: 'custom' as const }
            // Đã chọn khoảng nhanh thì ngày lẻ còn sót trên URL không có hiệu lực.
            if (values.period !== 'custom' && hasDates)
                return { ...values, date_from: '', date_to: '' }
            return values
        },
    },
)

export type OrderListValues = FilterValues<typeof ORDER_LIST_FILTERS.fields>

/** Khoảng ngày tạo đang áp dụng; `undefined` = toàn thời gian. */
export function resolveOrderPeriod(
    values: OrderListValues,
): { from: string; to: string } | undefined {
    if (values.period === 'all') return undefined
    if (values.period === 'custom')
        return values.date_from || values.date_to
            ? { from: values.date_from, to: values.date_to }
            : undefined
    return getPresetRange(values.period)
}

/**
 * Tìm kiếm giữ kỳ đang chọn như KiotViet — ô Thời gian luôn nói đúng khoảng
 * đang tìm, số phiếu viết tay lặp lại mỗi tháng không kéo theo đơn cũ. Đơn tạo
 * trước kỳ (dồn vào đầu tháng, khi tìm theo SĐT) cần lối "Tìm toàn thời gian"
 * luôn hiện: danh sách có thể không rỗng mà vẫn thiếu đúng đơn cần tìm.
 */
export const searchLimitedByPeriod = (values: OrderListValues) =>
    values.q !== '' && resolveOrderPeriod(values) !== undefined

const dmy = (ymd: string) => ymd.split('-').reverse().join('/')

/** "Tháng này (01/09/2026 – 15/09/2026)" — cho người dùng thấy đang xem kỳ nào. */
export function describeOrderPeriod(values: OrderListValues): string {
    const range = resolveOrderPeriod(values)
    if (!range) return periodLabel(values.period)
    const text = m.order_period_range({
        from: range.from ? dmy(range.from) : '…',
        to: range.to ? dmy(range.to) : '…',
    })
    return values.period === 'custom'
        ? text
        : `${periodLabel(values.period)} (${text})`
}

const noneIfAll = (value: string) =>
    value === 'all' || value === MULTI_CHOICE_ALL ? undefined : value

/** Bộ lọc → tham số GET /sales-orders. Dòng tổng luôn đi kèm danh sách. */
export function orderListApiParams(
    values: OrderListValues,
    page: number,
): ListOrdersParams {
    const range = resolveOrderPeriod(values)
    const status = noneIfAll(values.status)
    return {
        search: values.q || undefined,
        status,
        // Mỗi đơn thuộc đúng một ô tick: "Đang giao" không gồm đơn VTP đang
        // hoàn về, ô đó có giá trị riêng trong status (M-86).
        split_vtp_returning: status ? '1' : undefined,
        payment_status: noneIfAll(values.payment_status),
        einvoice_status: noneIfAll(values.einvoice_status),
        settlement_intent: noneIfAll(values.settlement_intent),
        source_channel: noneIfAll(values.source_channel),
        shop_channel: noneIfAll(values.shop_channel) as
            ListOrdersParams['shop_channel'] | undefined,
        created_by: noneIfAll(values.created_by),
        shipping_carrier_id: noneIfAll(values.shipping_carrier_id),
        sla_state: noneIfAll(values.sla_state),
        has_lot_debt: values.has_lot_debt === 'owing' ? '1' : undefined,
        kiotviet_unlinked:
            values.kiotviet_unlinked === 'pending' ? '1' : undefined,
        quick_sale: values.quick_sale === 'only' ? '1' : undefined,
        date_from: range?.from || undefined,
        date_to: range?.to || undefined,
        delivered_from: values.delivered_from || undefined,
        delivered_to: values.delivered_to || undefined,
        page,
        per_page: ORDERS_PER_PAGE,
        with_totals: '1',
    }
}
